import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";

const acceptedFormats = {
  "audio/wav": [".wav"],
  "audio/flac": [".flac"],
  "audio/aiff": [".aiff"],
  "audio/aif": [".aif"],
};

const MIN_FILES = 2;
const MAX_FILES = 32;

const MixesFileUpload = ({ onContinue }) => {
  const [files, setFiles] = useState([]);
  const [folderFiles, setFolderFiles] = useState([]);
  const [showFolderBrowser, setShowFolderBrowser] = useState(false);
  const [selectedFolderFiles, setSelectedFolderFiles] = useState(new Set());

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (!acceptedFiles?.length) return;

      if (files.length >= MAX_FILES) {
        toast.error(`You can upload up to ${MAX_FILES} files only.`, {
          position: "top-center",
          hideProgressBar: true,
        });
        return;
      }

      const file = acceptedFiles[0];

      if (files.some((f) => f.name === file.name && f.size === file.size)) {
        toast.error("This file is already added.", {
          position: "top-center",
          hideProgressBar: true,
        });
        return;
      }

      setFiles((prev) => [...prev, file]);
    },
    [files]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptedFormats,
    multiple: false,
  });

  const isValidAudioFile = (file) => {
    const ext = "." + file.name.split(".").pop().toLowerCase();
    return Object.values(acceptedFormats).flat().includes(ext);
  };

  const handleFolderSelect = (e) => {
    const uploadedFiles = Array.from(e.target.files);

    if (!uploadedFiles.length) return;

    const validFiles = uploadedFiles.filter(isValidAudioFile);

    const filesWithValidity = uploadedFiles.map((file, index) => ({
      file,
      id: `${file.name}-${file.size}-${index}`,
      isValid: isValidAudioFile(file),
    }));

    if (validFiles.length === 0) {
      toast.error("No valid audio files found in the folder.", {
        position: "top-center",
        hideProgressBar: true,
      });
      e.target.value = "";
      return;
    }

    if (validFiles.length < MIN_FILES) {
      toast.warning(
        `Folder contains only ${validFiles.length} valid audio file(s). You need at least ${MIN_FILES}.`,
        {
          position: "top-center",
          hideProgressBar: true,
        }
      );
    }

    // Auto-select all valid files
    const autoSelected = new Set(
      filesWithValidity
        .filter((item) => item.isValid)
        .map((item) => item.id)
    );

    setFolderFiles(filesWithValidity);
    setSelectedFolderFiles(autoSelected);
    setShowFolderBrowser(true);

    // Reset input
    e.target.value = "";
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFolderFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        if (newSet.size >= MAX_FILES) {
          toast.error(`You can only select up to ${MAX_FILES} files.`, {
            position: "top-center",
            hideProgressBar: true,
          });
          return prev;
        }
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  // Select all valid files
  const selectAllValidFiles = () => {
    const validFileIds = folderFiles
      .filter((item) => item.isValid)
      .map((item) => item.id);

    if (validFileIds.length > MAX_FILES) {
      toast.error(`Cannot select all. Maximum ${MAX_FILES} files allowed.`, {
        position: "top-center",
        hideProgressBar: true,
      });
      return;
    }

    setSelectedFolderFiles(new Set(validFileIds));
  };

  // Deselect all files
  const deselectAllFiles = () => {
    setSelectedFolderFiles(new Set());
  };

  // Confirm folder file selection
  const confirmFolderSelection = () => {
    const selectedFiles = folderFiles
      .filter((item) => selectedFolderFiles.has(item.id))
      .map((item) => item.file);

    if (selectedFiles.length < MIN_FILES) {
      toast.error(`Please select at least ${MIN_FILES} files.`, {
        position: "top-center",
        hideProgressBar: true,
      });
      return;
    }

    if (selectedFiles.length > MAX_FILES) {
      toast.error(`Please select no more than ${MAX_FILES} files.`, {
        position: "top-center",
        hideProgressBar: true,
      });
      return;
    }

    setFiles(selectedFiles);
    setShowFolderBrowser(false);
    setFolderFiles([]);
    setSelectedFolderFiles(new Set());

    toast.success(`${selectedFiles.length} files added successfully!`, {
      position: "top-center",
      hideProgressBar: true,
    });
  };

  const cancelFolderBrowser = () => {
    setShowFolderBrowser(false);
    setFolderFiles([]);
    setSelectedFolderFiles(new Set());
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleContinue = () => {
    if (files.length < MIN_FILES) {
      toast.error(`Please upload at least ${MIN_FILES} files.`, {
        position: "top-center",
        hideProgressBar: true,
      });
      return;
    }

    onContinue(files);
  };

  return (
    <div className="w-full max-w-[360px] mx-auto" data-tour="upload-section">
      {/* Folder Browser Modal */}
      {showFolderBrowser && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1a1a] rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-white text-xl font-bold mb-2">
                Select Files from Folder
              </h2>
              <p className="text-gray-400 text-sm">
                Selected: {selectedFolderFiles.size} / {MAX_FILES} files (Min: {MIN_FILES})
              </p>
            </div>

            {/* Action Buttons */}
            <div className="px-6 py-3 border-b border-gray-700 flex gap-2">
              <button
                onClick={selectAllValidFiles}
                className="px-4 py-2 bg-[#FA6138] text-white text-sm rounded-lg
                hover:bg-[#ff724d] transition-colors"
              >
                Select All Valid
              </button>
              <button
                onClick={deselectAllFiles}
                className="px-4 py-2 bg-gray-600 text-white text-sm rounded-lg
                hover:bg-gray-500 transition-colors"
              >
                Deselect All
              </button>
            </div>

            {/* Files List */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-2">
                {folderFiles.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => item.isValid && toggleFileSelection(item.id)}
                    className={`flex items-center justify-between p-4 rounded-xl
                    transition-all ${!item.isValid
                        ? "bg-gray-800 opacity-50 cursor-not-allowed"
                        : selectedFolderFiles.has(item.id)
                          ? "bg-[#FA6138] bg-opacity-20 border-2 border-[#FA6138] cursor-pointer"
                          : "bg-[#2a2a2a] border-2 border-transparent hover:border-gray-600 cursor-pointer"
                      }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* Checkbox */}
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center
                        ${!item.isValid
                            ? "border-gray-600 bg-gray-700"
                            : selectedFolderFiles.has(item.id)
                              ? "border-[#FA6138] bg-[#FA6138]"
                              : "border-gray-500"
                          }`}
                      >
                        {selectedFolderFiles.has(item.id) && (
                          <svg
                            className="w-3 h-3 text-white"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path d="M5 13l4 4L19 7"></path>
                          </svg>
                        )}
                      </div>

                      {/* File Info */}
                      <div className="flex flex-col flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-sm font-medium truncate ${item.isValid ? "text-white" : "text-gray-500"
                              }`}
                          >
                            {item.file.name}
                          </span>
                          {!item.isValid && (
                            <span className="text-xs bg-red-900 text-red-200 px-2 py-0.5 rounded">
                              Unsupported
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-400">
                          {(item.file.size / (1024 * 1024)).toFixed(2)} MB
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="p-6 border-t border-gray-700 flex gap-3">
              <button
                onClick={cancelFolderBrowser}
                className="flex-1 py-3 bg-gray-600 text-white rounded-full
                hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmFolderSelection}
                disabled={selectedFolderFiles.size < MIN_FILES}
                className={`flex-1 py-3 rounded-full text-white transition-colors ${selectedFolderFiles.size < MIN_FILES
                    ? "bg-gray-500 cursor-not-allowed"
                    : "bg-[#FA6138] hover:bg-[#ff724d]"
                  }`}
              >
                Add {selectedFolderFiles.size} File{selectedFolderFiles.size !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Dropzone */}
      <div
        {...getRootProps()}
        className={`relative border-2 border-dashed border-[#FA6138] rounded-full aspect-square p-8
           bg-[#741F45]
           transition-all duration-300 cursor-pointer
           hover:border-[#ff724d] group`}
      >
        <input {...getInputProps()} />

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
          <img
            src={require("../../../../assets/images/icons/upload-icon.png")}
            alt="Upload"
            className="block w-[50px]"
          />

          <p className="text-white text-sm mt-4">
            {files.length === 0
              ? "Upload your first track"
              : "Upload another track"}
          </p>

          <button
            type="button"
            className="bg-[#FA6138] text-white px-8 py-2 rounded-full mt-4
            hover:bg-[#ff724d] transition-colors"
          >
            Select file
          </button>

          <p className="text-white text-sm font-bold opacity-80 mt-4">
            Accepted formats: WAV, FLAC, AIFF
          </p>
          <p className="text-white text-sm opacity-80">
            Your track will be securely analyzed
            <br />
            to generate a free mix preview.
          </p>
        </div>
      </div>

      {/* OR Divider */}
      <div className="flex items-center justify-center my-4">
        <div className="flex-1 border-t border-gray-500"></div>
        <span className="px-3 text-gray-400 text-sm">OR</span>
        <div className="flex-1 border-t border-gray-500"></div>
      </div>

      {/* Folder Upload Button */}
      <label
        htmlFor="folder-upload"
        className="block w-full text-center bg-[#741F45] border-2 border-[#FA6138] 
        text-white py-3 px-6 rounded-full cursor-pointer
        hover:bg-[#8a2451] hover:border-[#ff724d] transition-all"
      >
        📁 Browse & Select from Folder
      </label>
      <input
        id="folder-upload"
        type="file"
        webkitdirectory=""
        directory=""
        multiple
        onChange={handleFolderSelect}
        className="hidden"
      />

      <p className="text-center text-xs text-gray-400 mt-2">
        Select {MIN_FILES}-{MAX_FILES} files from a folder
      </p>

      {/* Uploaded files list */}
      {files.length > 0 && (
        <div className="mt-6 space-y-2">
          <div className="flex justify-between items-center mb-2">
            <span className="text-white text-sm font-medium">
              Uploaded Files ({files.length}/{MAX_FILES})
            </span>
          </div>

          <div className="space-y-2 max-h-[250px] overflow-y-auto">
            {files.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between
                bg-theme_clr1 text-white px-4 py-3 rounded-xl"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium truncate max-w-[240px]">
                    {file.name}
                  </span>
                  <span className="text-xs text-gray-300">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </span>
                </div>

                <button
                  onClick={() => removeFile(index)}
                  className="text-red-400 text-sm hover:text-red-300"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Continue CTA */}
      <button
        onClick={handleContinue}
        disabled={files.length < MIN_FILES}
        className={`w-full mt-6 py-3 rounded-full text-white
        transition-all ${files.length < MIN_FILES
            ? "bg-gray-500 cursor-not-allowed"
            : "bg-[#FA6138] hover:bg-[#ff724d]"
          }`}
      >
        Continue ({files.length}/{MAX_FILES})
      </button>

      <p className="text-center text-sm text-gray-300 mt-2">
        Upload at least {MIN_FILES} tracks to continue
      </p>

      <p className="text-center text-xs text-gray-300 mt-3">
        Note: Processing can take up to 15 minutes from upload to final mix,
        depending on the number of files submitted. Thank you for your patience
        while our engineers work on perfecting your mix.
      </p>

    </div>
  );
};

export default MixesFileUpload;